import { isTouchDevice } from "common/utils/browser";
import { on as onEvent } from "common/utils/ui/event";

// Edge detection utilities
export const getViewportDimensions = () => ({
  width: window.innerWidth || document.documentElement.clientWidth,
  height: window.innerHeight || document.documentElement.clientHeight
});

export const getElementBounds = element => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height
  };
};

// Smart placement function for Bootstrap tooltip
export const getSmartPlacement = (tooltip, element) => {
  const viewport = getViewportDimensions();
  const elementBounds = getElementBounds(element);

  // Estimated tooltip dimensions (approximate)
  const tooltipWidth = 320; // Based on typical content width
  const tooltipHeight = 280; // Based on typical content height
  const arrowOffset = 10; // Arrow size offset

  // Calculate available space in each direction
  const spaceAbove = elementBounds.top;
  const spaceBelow = viewport.height - elementBounds.bottom;
  const spaceLeft = elementBounds.left;
  const spaceRight = viewport.width - elementBounds.right;

  // Determine best vertical placement
  let verticalPlacement = "top";
  if (spaceAbove < tooltipHeight + arrowOffset && spaceBelow > spaceAbove) {
    verticalPlacement = "bottom";
  }

  // Determine best horizontal alignment
  let horizontalPlacement = "";
  const elementCenter = elementBounds.left + elementBounds.width / 2;
  const tooltipHalfWidth = tooltipWidth / 2;

  // Check if tooltip would overflow on the right
  if (elementCenter + tooltipHalfWidth > viewport.width - 20) {
    horizontalPlacement = "-end";
  }
  // Check if tooltip would overflow on the left
  else if (elementCenter - tooltipHalfWidth < 20) {
    horizontalPlacement = "-start";
  }

  // If there's not enough space above or below, try left/right
  if (spaceAbove < tooltipHeight + arrowOffset && spaceBelow < tooltipHeight + arrowOffset) {
    if (spaceRight > tooltipWidth + arrowOffset) {
      return "right";
    }

    if (spaceLeft > tooltipWidth + arrowOffset) {
      return "left";
    }
  }

  return verticalPlacement + horizontalPlacement;
};

// Additional positioning adjustment after tooltip is shown
// eslint-disable-next-line max-lines-per-function
export const adjustTooltipPosition = triggerElement => {
  const tooltipId = triggerElement.getAttribute("aria-describedby");
  if (!tooltipId) return;

  const tooltipElement = document.getElementById(tooltipId);
  if (!tooltipElement) return;

  const viewport = getViewportDimensions();
  const tooltipBounds = getElementBounds(tooltipElement);

  // Check if tooltip is going outside viewport boundaries
  let adjustmentNeeded = false;
  const adjustments = { top: 0, left: 0 };

  // Check right boundary
  if (tooltipBounds.right > viewport.width - 10) {
    adjustments.left = viewport.width - tooltipBounds.right - 10;
    adjustmentNeeded = true;
  }

  // Check left boundary
  if (tooltipBounds.left < 10) {
    adjustments.left = 10 - tooltipBounds.left;
    adjustmentNeeded = true;
  }

  // Check bottom boundary
  if (tooltipBounds.bottom > viewport.height - 10) {
    adjustments.top = viewport.height - tooltipBounds.bottom - 10;
    adjustmentNeeded = true;
  }

  // Check top boundary
  if (tooltipBounds.top < 10) {
    adjustments.top = 10 - tooltipBounds.top;
    adjustmentNeeded = true;
  }

  // Apply adjustments if needed
  if (adjustmentNeeded) {
    const currentTransform = tooltipElement.style.transform || "";
    const currentTranslate = currentTransform.match(/translate(?:3d)?\(([^)]+)\)/);

    let currentX = 0;
    let currentY = 0;
    if (currentTranslate) {
      const values = currentTranslate[1].split(",").map(v => parseFloat(v.trim()));
      currentX = values[0] || 0;
      currentY = values[1] || 0;
    }

    const newX = currentX + adjustments.left;
    const newY = currentY + adjustments.top;

    tooltipElement.style.transform = `translate(${newX}px, ${newY}px)`;
  }
};

/*  Bootstrap tooltips to stay open on hover https://stackoverflow.com/a/36056707 */
export const initStayOpenOnHover = () => {
  const originalLeave = $.fn.tooltip.Constructor.prototype.leave;

  $.fn.tooltip.Constructor.prototype.leave = function leave(event) {
    const { currentTarget } = event;
    const options = this.getDelegateOptions();
    const dynamicDataValue = $(currentTarget)[this.type](options).data(`bs.${this.type}`);
    const self = event instanceof this.constructor ? event : dynamicDataValue;

    originalLeave.call(this, event);

    if (currentTarget) {
      const $$container = $(`#${$(currentTarget).attr("aria-describedby")}`);
      const hasLink = !!$("a[href]", $$container).length;
      if (!hasLink) return;

      const { timeout } = self;
      $$container.one("mouseenter", () => {
        clearTimeout(timeout);

        $$container.one("mouseleave", () => {
          $.fn.tooltip.Constructor.prototype.leave.call(self, self);
        });
      });
    }
  };
};

export const initTooltipClose = (closeSelector, getTooltipsFn, triggerSelector) => {
  const callback = e => {
    e.preventDefault();
    getTooltipsFn().tooltip("hide");
  };

  const removeCloseListener = onEvent("click", closeSelector, callback, true);

  let removeTouchListener = null;

  // On touch devices, close tooltip when clicking outside
  if (isTouchDevice()) {
    const touchHandler = e => {
      // Check if click is outside tooltip and trigger element
      const isTooltipClick = e.target.closest(".tooltip");
      const isTriggerClick = e.target.closest(triggerSelector);

      if (!isTooltipClick && !isTriggerClick) {
        getTooltipsFn().tooltip("hide");
      }
    };

    document.addEventListener("click", touchHandler, true);
    removeTouchListener = () => document.removeEventListener("click", touchHandler, true);
  }

  return () => {
    removeCloseListener();
    if (removeTouchListener) removeTouchListener();
  };
};

const bubblingGuard = new WeakSet();

export const stopBubbling = getTooltipsFn => {
  getTooltipsFn().forEach($badge => {
    if (bubblingGuard.has($badge)) return;
    bubblingGuard.add($badge);

    $badge.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
    });
  });
};

export const setupTouchTooltips = ($$tooltips, getTooltipsFn) => {
  $$tooltips.each(function attachClickHandler() {
    const element = this;
    $(element).on("click", e => {
      e.preventDefault();
      e.stopPropagation();

      const $element = $(element);
      const isShown = $element.attr("aria-describedby");

      // Hide all other tooltips — query fresh so dynamically added tooltips are included
      getTooltipsFn().not(element).tooltip("hide");

      // Toggle current tooltip
      $element.tooltip(isShown ? "hide" : "show");
    });
  });
};
